import { ComboBox } from '../../../credit/ui/comboBox/ComboBox';
import { CreditAppService } from 'src/services/credit-app.service';
import { filterFinanceTypes } from '../../../../utils/credit.helpers';
import FormSectionPage from '../../ui/formSectionPage';
import i18n from 'i18next';
import { IUser } from '../../../../utils/interfaces/user';
import TooltipIcon from '../../../ui/tooltip';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import {
    removeLeadingZeros,
    validateCarYear,
    validateLengthNumber,
    validateRangeCeroToNineNine,
    validateRangeFourToNineNine,
    validateRangeToTradeAmount,
} from '../utils';
import './styles.scss';
export const financialTypes = {
    LOAN: 'Loan',
    LEASE: 'Lease',
    BALLOON: 'Balloon',
};

const countries = {
    USA: 'USA',
    CA: 'Canada',
};

const brands = {
    VW: 'vw',
    AUDI: 'audi',
    DUCATI: 'ducati',
};

const YEAR_SUS = 2;
const ZERO = 0;
const ONE = 1;
const FOUR_THOUSAND = 4000;
interface IProps {
    vehicleType: string;
    year: number;
    setFinancialData: Function;
    setFinanceValid: Function;
    isOpen: boolean;
    setOpen: Function;
    make: string;
    isBlocked?: boolean;
    initialFinance?: any;
    specialPrograms?: any;
    isEditMode?: boolean;
    disablePrintAndSubmitBtn: Function;
}

interface IItemLabelProps {
    label: string;
    tooltip?: string;
    style?: React.CSSProperties;
    normalFit?: string;
}

interface IComboProps extends IItemLabelProps, IErrorLabelProps {
    options: IOption[];
    selectedOption: string;
    isError?: boolean;
    onSelectHandler: Function;
    defaultValue: string;
    placeHolder?: string;
    name: string;
    isDisabled: boolean;
}

interface IErrorLabelProps {
    errorLabel?: string;
}

interface IOption {
    value: string;
    label: string;
}

interface IInputProps extends IItemLabelProps, IErrorLabelProps {
    value: string | number;
    isError: boolean;
    isReadOnly?: boolean;
    onChangeHandler: Function;
    onBlurHandler?: Function;
    maxLength?: number;
    isDisabled?: boolean;
    min?: number;
    max?: number;
    type?: string;
    step?: string;
    hideSpinner?: boolean;
}

const ErrorLabel: React.FC<IErrorLabelProps> = ({ errorLabel }) => {
    return (
        <span
            className={'c-error-message'}
            style={{
                position: 'absolute',
            }}
        >
            {errorLabel}
        </span>
    );
};

const ItemLabel: React.FC<IItemLabelProps> = ({ label, tooltip, style }) => {
    return (
        <div>
            <label
                htmlFor='m1lh7pdb'
                className='c-form-field__label'
                style={style}
            >
                {label}
            </label>
            {tooltip && (
                <>
                    <span>&nbsp;</span>
                    <TooltipIcon text={tooltip}></TooltipIcon>
                </>
            )}
        </div>
    );
};

const LayoutItem: React.FC<IItemLabelProps> = ({
    label,
    tooltip,
    children,
    normalFit = '1/2',
}) => {
    return (
        <div className={`o-layout__item  u-${normalFit}  u-1/1@xs`}>
            <div className='c-form-field c-form-field--no-floating'>
                <ItemLabel label={label} tooltip={tooltip} />
                <div className='c-form-field__box'>{children}</div>
            </div>
        </div>
    );
};

const LayoutCombo: React.FC<IComboProps> = ({
    label,
    selectedOption,
    options,
    errorLabel = '',
    isError = false,
    onSelectHandler,
    name,
    tooltip = '',
    placeHolder = '',
    isDisabled = false,
    defaultValue = '',
}) => {
    return (
        <LayoutItem label={label} tooltip={tooltip}>
            <ComboBox
                name={name}
                options={options}
                defaultValue={defaultValue}
                placeholder={placeHolder}
                onSelect={(v) => onSelectHandler(v)}
                selectedOption={selectedOption}
                disabled={isDisabled}
            />
            <div>{isError ? <ErrorLabel errorLabel={errorLabel} /> : null}</div>
        </LayoutItem>
    );
};

const LayoutInput: React.FC<IInputProps> = ({
    label,
    value,
    errorLabel = '',
    isReadOnly = false,
    isError,
    onChangeHandler,
    onBlurHandler,
    tooltip = '',
    maxLength = 10,
    isDisabled,
    min,
    max,
    type = 'number',
    normalFit,
    step,
    hideSpinner = false,
}) => {
    return (
        <LayoutItem label={label} tooltip={tooltip} normalFit={normalFit}>
            <div className='c-input'>
                <input
                    className={`c-input__input ${isError ? 'is-error' : ''} ${
                        isReadOnly ? 'bold' : ''
                    } ${hideSpinner ? 'c-input__input--no-spinner' : ''}`}
                    onChange={(e) => onChangeHandler(e)}
                    onBlur={(e) => {
                        if (onBlurHandler) onBlurHandler(e);
                    }}
                    type={type}
                    value={value}
                    maxLength={maxLength}
                    readOnly={isReadOnly}
                    disabled={isDisabled}
                    min={min}
                    max={max}
                    step={step}
                />
            </div>
            <div>{isError ? <ErrorLabel errorLabel={errorLabel} /> : null}</div>
        </LayoutItem>
    );
};
const FinancialInfo = ({
    vehicleType = 'NEW',
    year,
    setFinancialData,
    setFinanceValid,
    isOpen,
    setOpen,
    make,
    isBlocked = false,
    initialFinance = null,
    specialPrograms = null,
    isEditMode = false,
    disablePrintAndSubmitBtn,
}: IProps) => {
    const { financeSection, creditApp } = useLocalization();
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const { markCreditFormTouched } = useCreditForm();

    const specialProgramOptionsTitle = {
        BrokerDeal: financeSection.SPECIAL_PROGRAM_ITEM_BROKER,
        CollegeGrad: financeSection.SPECIAL_PROGRAM_ITEM_COLLEGE_GRAD,
        ForeignBusinessProfessional:
            financeSection.SPECIAL_PROGRAM_ITEM_FOREIGN_BUS,
        ForeignCollegeStudent:
            financeSection.SPECIAL_PROGRAM_ITEM_FOREIGN_STUDENT,
        FirstTimeBuyer: financeSection.SPECIAL_PROGRAM_ITEM_FIRST_TIME,
        DeferredPayment: financeSection.SPECIAL_PROGRAM_ITEM_DEF_PAYMENT,
        LeaseTransferOrTransferOfEquity:
            financeSection.SPECIAL_PROGRAM_ITEM_LEASE_TRANSFER,
        RateProtection: financeSection.SPECIAL_PROGRAM_ITEM_RATE_PROTECTION,
        PrepayLease: financeSection.SPECIAL_PROGRAM_ITEM_PRE_PAY_LEASE,
        MultipleSecurityDeposit:
            financeSection.SPECIAL_PROGRAM_ITEM_MULTI_SEC_DEPOSIT,
        ForeignWorker: financeSection.SPECIAL_PROGRAM_ITEM_FOREIGN_WORKER,
        VGCAEmployee: financeSection.SPECIAL_PROGRAM_ITEM_VGCA_EMPLOYEE,
        AmbassadorProgram:
            financeSection.SPECIAL_PROGRAM_ITEM_AMBASSADOR_PROGRAM,
        PermanentResident:
            financeSection.SPECIAL_PROGRAM_ITEM_PERMANENT_RESIDENT,
    };
    type SpecialProgramKeys = keyof typeof specialProgramOptionsTitle;

    const [disabledForm, setDisabledForm] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean | null>(null);
    const [financeType, setFinanceType] = useState(
        initialFinance?.FinanceType ?? financialTypes.LEASE,
    );
    const [financeOptions, setFinanceOptions] = useState<any[]>([]);
    const [isRetailAndOMSCDealer, setIsRetailAndOMSCDealer] =
        useState<boolean>(false);
    const [term, setTerm] = useState<number | ''>(initialFinance?.Term ?? '');
    const [errorTerm, setErrorTerm] = useState(false);
    const [netTradeInAmount, setNetTradeInAmount] = useState(
        initialFinance?.NetTradeInAmount ?? ZERO,
    );
    const [errorTradeIn, setErrorTradeIn] = useState(false);
    const [tradeInYear, setTradeInYear] = useState<number | ''>(
        initialFinance?.TradeInVehicle?.Year &&
            initialFinance?.TradeInVehicle?.Year !== ZERO
            ? initialFinance?.TradeInVehicle?.Year
            : '',
    );
    const [errorTradeInYear, setErrorTradeInYear] = useState(false);
    const [tradeInYearRequired, setTradeInYearRequired] = useState(false);
    const [tradeInMake, setTradeInMake] = useState(
        initialFinance?.TradeInVehicle?.Make ?? '',
    );
    const [errorTradeInMake, setErrorTradeInMake] = useState(false);
    const [tradeInMakeRequired, setTradeInMakeRequired] = useState(false);
    const [tradeInModel, setTradeInModel] = useState(
        initialFinance?.TradeInVehicle?.Model ?? '',
    );
    const [errorTradeInModel, setErrorTradeInModel] = useState(false);
    const [tradeInModelRequired, setTradeInModelRequired] = useState(false);
    const [tradeInBody, setTradeInBody] = useState(
        initialFinance?.TradeInVehicle?.BodyType ?? '',
    );
    const [errorTradeInBody, setErrorTradeInBody] = useState(false);
    const [tradeInBodyRequired, setTradeInBodyRequired] = useState(false);
    const [optionOne, setOptionOne] = useState('');
    const [optionsOneSP, setOptionsOneSP] = useState<any[]>([]);
    const [optionTwo, setOptionTwo] = useState('');
    const [optionsTwoSP, setOptionsTwoSP] = useState<any[]>([]);
    const [loanAmount, setLoanAmount] = useState(ZERO);
    const [loanAmountError, setLoanAmountError] = useState(false);
    const [purchasePrice, setPurchasePrice] = useState(
        removeLeadingZeros(initialFinance?.CashPrice ?? ZERO),
    );
    const [errorPurchasePrice, setErrorPurchasePrice] = useState(false);
    const [downPayment, setDownPayment] = useState(
        removeLeadingZeros(initialFinance?.DownPayment ?? ZERO),
    );
    const [errorDownPayment, setErrorDownPayment] = useState(false);
    const [monthlyPayment, setMonthlyPayment] = useState(
        removeLeadingZeros(initialFinance?.MonthlyPayment ?? ZERO),
    );
    const [errorMonthlyPayment, setErrorMonthlyPayment] = useState(false);
    const [msrp, setMSRP] = useState(
        removeLeadingZeros(initialFinance?.MSRP ?? ZERO),
    );

    const [errorMSRP, setErrorMSRP] = useState(false);
    const [rebateAmount, setRebateAmount] = useState(ZERO);
    const [rebateAmountError, setRebateAmountError] = useState(false);
    const [grossCapCost, setGrossCapCost] = useState(
        removeLeadingZeros(initialFinance?.GrossCapCost ?? ZERO),
    );
    const [errorGrossCap, setErrorGrossCap] = useState(false);
    const [adjustedCapCost, setAdjustedCapCost] = useState(
        removeLeadingZeros(initialFinance?.AdjustedCapCost ?? ZERO),
    );
    const [errorAdjustCap, setErrorAdjustCap] = useState(false);

    useEffect(() => {
        setErrorPurchasePrice(validateRangeCeroToNineNine(purchasePrice));
    }, [purchasePrice]);

    useEffect(() => {
        setErrorMSRP(validateRangeCeroToNineNine(msrp));
    }, [msrp]);

    useEffect(() => {
        setErrorGrossCap(validateRangeCeroToNineNine(grossCapCost));
    }, [grossCapCost]);

    useEffect(() => {
        setErrorAdjustCap(validateRangeCeroToNineNine(adjustedCapCost));
    }, [adjustedCapCost]);

    useEffect(() => {
        if (netTradeInAmount == '') {
            setNetTradeInAmount(ZERO);
        }
        setErrorTradeIn(validateRangeToTradeAmount(netTradeInAmount));
    }, [netTradeInAmount]);

    useEffect(() => {
        setErrorTradeInMake(validateLengthNumber(tradeInMake));
    }, [tradeInMake]);

    useEffect(() => {
        setErrorTradeInYear(
            tradeInYear !== '' && validateCarYear(Number(tradeInYear)),
        );
    }, [tradeInYear]);

    useEffect(() => {
        setErrorTradeInModel(validateLengthNumber(tradeInModel));
    }, [tradeInModel]);

    useEffect(() => {
        setErrorTradeInBody(validateLengthNumber(tradeInBody));
    }, [tradeInBody]);

    useEffect(() => {
        setErrorDownPayment(validateRangeCeroToNineNine(downPayment));
    }, [downPayment]);

    useEffect(() => {
        setErrorMonthlyPayment(validateRangeCeroToNineNine(monthlyPayment));
    }, [monthlyPayment]);

    useEffect(() => {
        if (vehicleType && !initialFinance) {
            setMSRP(ZERO);
        }
    }, [vehicleType]);

    useEffect(() => {
        if (dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            const { brand, country } = dealer;

            populateLeft(brand, country);
            populateRight(brand, country, year);
        }
    }, []);
    useEffect(() => {
        if (dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            const { brand, country } = dealer;

            populateLeft(brand, country);
            populateRight(brand, country, year);
        }
    }, [i18n.language]);

    async function getDealerAllowedToFinance(dealerId: string) {
        const service = new CreditAppService();
        const res = await service.getDealerAllowedToFinance(dealerId);
        if (
            res &&
            res.data &&
            res.data.IsOmscDealer &&
            res.data.retailAllowed
        ) {
            setIsRetailAndOMSCDealer(true);
        }
    }

    useEffect(() => {
        if (dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            const { brand, country, stateProvince } = dealer;
            const options = filterFinanceTypes(
                make,
                vehicleType,
                brand,
                country,
                year,
                stateProvince,
            ).map((ft) => {
                return {
                    value: ft.value,
                    label: parseFinancyType(ft.value),
                };
            });
            const hasLoan = options.some(
                ({ value }) => value === financialTypes.LOAN,
            );

            if (isRetailAndOMSCDealer && !hasLoan) {
                options.push({
                    value: financialTypes.LOAN,
                    label: financialTypes.LOAN,
                });
            }
            setFinanceOptions(options);
        }
    }, [vehicleType, year, make, i18n.language, isRetailAndOMSCDealer]);

    useEffect(() => {
        if (dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            if (dealer.country === countries.USA) {
                getDealerAllowedToFinance(dealer.dealerNumber);
            } else {
                setIsRetailAndOMSCDealer(false);
            }
        }
    }, [dealerDataInfo]);

    useEffect(() => {
        if (financeOptions.length) {
            if (initialFinance?.FinanceType)
                setFinanceType(initialFinance?.FinanceType);
            else setFinanceType(financeOptions[0].value);
        }
    }, [financeOptions]);

    useEffect(() => {
        if (dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            const { brand, country } = dealer;

            populateLeft(brand, country);
            populateRight(brand, country, year);
        }
    }, [financeType]);

    useEffect(() => {
        if (financeType === financialTypes.LEASE)
            setRebateAmount(grossCapCost - adjustedCapCost);
    }, [grossCapCost, adjustedCapCost]);

    useEffect(() => {
        if (
            financeType === financialTypes.LOAN ||
            financeType === financialTypes.BALLOON
        ) {
            setLoanAmount(purchasePrice - netTradeInAmount - downPayment);
        }
    }, [purchasePrice, netTradeInAmount, downPayment]);

    const getTitle = (index: number) => {
        const label = (specialPrograms[index] as SpecialProgramKeys) ?? '';
        const title = specialProgramOptionsTitle[label];

        return title || '';
    };

    const foundOptionOne = (titleOne: string) => {
        const foundOptionOne = optionsOneSP.find(
            (option) => option.label === titleOne,
        );

        return foundOptionOne;
    };

    const foundOptionTwo = (titleTwo: string) => {
        const foundOptionTwo = optionsTwoSP.find(
            (option) => option.label === titleTwo,
        );

        return foundOptionTwo;
    };

    const setOneSpecialProgramOption = (title: string) => {
        const optionOne = foundOptionOne(title);

        if (optionOne) {
            setOptionOne(optionOne.value);
        }

        const optionTwo = foundOptionTwo(title);

        if (optionTwo) {
            setOptionTwo(optionTwo.value);
        }
    };

    const setProgramTitle = (index: number) =>
        setOneSpecialProgramOption(getTitle(index));

    //for edit mode
    useEffect(() => {
        if (!specialPrograms) return;

        if (Array.isArray(specialPrograms)) {
            if (specialPrograms.length > ONE) {
                setProgramTitle(ZERO);
                setProgramTitle(ONE);
            } else {
                setProgramTitle(ZERO);
            }
        } else {
            const title =
                specialProgramOptionsTitle[
                    specialPrograms as SpecialProgramKeys
                ];
            setOneSpecialProgramOption(title);
        }
    }, [optionsOneSP, optionsTwoSP]);

    function populateLeft(brand: string, country: string) {
        const validateAllBrand =
            brand.toLocaleLowerCase().includes(brands.VW) ||
            brand.toLocaleLowerCase().includes(brands.AUDI) ||
            brand.toLocaleLowerCase().includes(brands.DUCATI);
        const vwOrAudi =
            brand.toLocaleLowerCase().includes(brands.VW) ||
            brand.toLocaleLowerCase().includes(brands.AUDI);
        if (validateAllBrand) {
            if (
                country === countries.USA &&
                vehicleType.toUpperCase() === 'NEW' &&
                financeType === financialTypes.LOAN
            ) {
                setOptionsOneSP([
                    {
                        value: '',
                        label: creditApp.SELECT_OPTION,
                    },
                    {
                        value: '12',
                        label: specialProgramOptionsTitle.BrokerDeal,
                    },
                    {
                        value: '02',
                        label: specialProgramOptionsTitle.CollegeGrad,
                    },
                    {
                        value: '08',
                        label: specialProgramOptionsTitle.ForeignBusinessProfessional,
                    },
                    {
                        value: '10',
                        label: specialProgramOptionsTitle.ForeignCollegeStudent,
                    },
                    {
                        value: '03',
                        label: specialProgramOptionsTitle.FirstTimeBuyer,
                    },
                    {
                        value: '20',
                        label: specialProgramOptionsTitle.DeferredPayment,
                    },
                ]);
                return;
            }
        }

        if (vwOrAudi) {
            if (country === countries.USA) {
                setOptionsOneSP([
                    {
                        value: '',
                        label: creditApp.SELECT_OPTION,
                    },
                    {
                        value: '12',
                        label: specialProgramOptionsTitle.BrokerDeal,
                    },
                    {
                        value: '02',
                        label: specialProgramOptionsTitle.CollegeGrad,
                    },
                    {
                        value: '08',
                        label: specialProgramOptionsTitle.ForeignBusinessProfessional,
                    },
                    {
                        value: '10',
                        label: specialProgramOptionsTitle.ForeignCollegeStudent,
                    },
                    {
                        value: '03',
                        label: specialProgramOptionsTitle.FirstTimeBuyer,
                    },
                ]);
                return;
            }

            if (country === countries.CA) {
                setOptionsOneSP([
                    {
                        value: '',
                        label: creditApp.SELECT_OPTION,
                    },
                    {
                        value: '02',
                        label: specialProgramOptionsTitle.CollegeGrad,
                    },
                    {
                        value: '08',
                        label: specialProgramOptionsTitle.ForeignBusinessProfessional,
                    },
                    {
                        value: '10',
                        label: specialProgramOptionsTitle.ForeignCollegeStudent,
                    },
                    {
                        value: '14',
                        label: specialProgramOptionsTitle.ForeignWorker,
                    },
                    {
                        value: '03',
                        label: specialProgramOptionsTitle.FirstTimeBuyer,
                    },
                    {
                        value: '06',
                        label: specialProgramOptionsTitle.LeaseTransferOrTransferOfEquity,
                    },
                    {
                        value: '15',
                        label: specialProgramOptionsTitle.VGCAEmployee,
                    },
                ]);
                return;
            }
        }
    }

    function parseFinancyType(financeType: string): string {
        if (
            financeType
                .toLocaleUpperCase()
                .includes(financialTypes.LEASE.toLocaleUpperCase())
        )
            return financeSection.OPTION_LEASE;
        if (
            financeType
                .toLocaleUpperCase()
                .includes(financialTypes.LOAN.toLocaleUpperCase())
        )
            return financeSection.OPTION_LOAN;
        if (
            financeType
                .toLocaleUpperCase()
                .includes(financialTypes.BALLOON.toLocaleUpperCase())
        )
            return dealerDataInfo?.dealer.country === 'CA' ||
                dealerDataInfo?.dealer.country === 'Canada'
                ? financeSection.OPTION_VWSELECT
                : financeSection.OPTION_DRIVER;
        return financeType;
    }

    function populateRight(brand: string, country: string, year: number) {
        const vwOrAudi =
            brand.toLocaleLowerCase().includes(brands.VW) ||
            brand.toLocaleLowerCase().includes(brands.AUDI);
        const date = new Date();
        const currentYear = date.getFullYear();
        if (vwOrAudi) {
            if (
                country === countries.CA &&
                financeType === financialTypes.LEASE
            ) {
                setOptionsTwoSP([
                    {
                        value: '',
                        label: creditApp.SELECT_OPTION,
                    },
                    {
                        value: '05',
                        label: specialProgramOptionsTitle.RateProtection,
                    },
                    {
                        value: '09',
                        label: specialProgramOptionsTitle.PrepayLease,
                    },
                    {
                        value: '01',
                        label: specialProgramOptionsTitle.MultipleSecurityDeposit,
                    },
                ]);
            } else
                setOptionsTwoSP([
                    {
                        value: '',
                        label: creditApp.SELECT_OPTION,
                    },
                    {
                        value: '05',
                        label: specialProgramOptionsTitle.RateProtection,
                    },
                ]);
        } else if (
            brand.toLocaleLowerCase().includes(brands.DUCATI) &&
            year > currentYear - YEAR_SUS &&
            year < currentYear + YEAR_SUS
        ) {
            setOptionsTwoSP([
                {
                    value: '05',
                    label: specialProgramOptionsTitle.RateProtection,
                },
            ]);
        }
    }

    const onEdit = () => {
        setDisabledForm(false);
        setFinanceValid(false);
        if (isEditMode) {
            disablePrintAndSubmitBtn(true);
        }
    };

    function handlerSubmit() {
        let allValid = true;
        clearErrors();
        if (term === '' || validateRangeFourToNineNine(term)) {
            setErrorTerm(true);
            evaluateValidated(false);
            allValid = false;
        }

        if (validateRangeCeroToNineNine(msrp)) {
            setErrorMSRP(true);
            evaluateValidated(false);
            allValid = false;
        }

        if (
            validateRangeCeroToNineNine(monthlyPayment) &&
            financialTypes.LEASE === financeType
        ) {
            setErrorMonthlyPayment(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateRangeCeroToNineNine(downPayment)) {
            setErrorDownPayment(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateRangeCeroToNineNine(adjustedCapCost)) {
            setErrorAdjustCap(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateRangeCeroToNineNine(grossCapCost)) {
            setErrorGrossCap(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateRangeCeroToNineNine(purchasePrice)) {
            setErrorPurchasePrice(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateLengthNumber(tradeInBody)) {
            setErrorTradeInBody(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateLengthNumber(tradeInModel)) {
            setErrorTradeInModel(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateLengthNumber(tradeInMake)) {
            setErrorTradeInMake(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (tradeInYear !== '' && validateCarYear(Number(tradeInYear))) {
            setErrorTradeInYear(true);
            evaluateValidated(false);
            allValid = false;
        }
        if (validateRangeToTradeAmount(netTradeInAmount)) {
            setErrorTradeIn(true);
            evaluateValidated(false);
            allValid = false;
        }

        if (
            (financeType === financialTypes.LOAN ||
                financeType === financialTypes.BALLOON) &&
            loanAmount < FOUR_THOUSAND
        ) {
            setLoanAmountError(true);
            evaluateValidated(false);
            allValid = false;
        } else if (
            financeType === financialTypes.LEASE &&
            adjustedCapCost < FOUR_THOUSAND
        ) {
            setRebateAmountError(true);
            evaluateValidated(false);
            allValid = false;
        } else {
            setLoanAmountError(false);
            setRebateAmountError(false);
        }

        if (
            (financeType === financialTypes.LOAN ||
                financeType === financialTypes.BALLOON) &&
            netTradeInAmount > ZERO
        ) {
            let escape = false;
            setTradeInBodyRequired(false);
            setTradeInModelRequired(false);
            setTradeInMakeRequired(false);
            setTradeInYearRequired(false);

            if (tradeInBody == '') {
                setTradeInBodyRequired(true);
                evaluateValidated(false);
                escape = true;
            }
            if (tradeInModel == '') {
                setTradeInModelRequired(true);
                evaluateValidated(false);
                escape = true;
            }
            if (tradeInMake == '') {
                setTradeInMakeRequired(true);
                evaluateValidated(false);
                escape = true;
            }
            if (tradeInYear === '' || tradeInYear === ZERO) {
                setTradeInYearRequired(true);
                evaluateValidated(false);
                escape = true;
            }
            if (escape) {
                allValid = false;
            }
        }

        if (allValid) {
            evaluateValidated(true);
        }
    }

    function clearErrors() {
        setErrorTerm(false);
        setErrorMSRP(false);
        setErrorMonthlyPayment(false);
        setErrorDownPayment(false);
        setErrorAdjustCap(false);
        setErrorGrossCap(false);
        setErrorPurchasePrice(false);
        setErrorTradeInBody(false);
        setErrorTradeInModel(false);
        setErrorTradeInMake(false);
        setErrorTradeInYear(false);
        setErrorTradeIn(false);
    }

    function buildAndSetFinancialData() {
        let financialData: any = {
            financeType,
            term,
            msrp,
        };

        const specialPrograms = [optionOne, optionTwo].filter(
            (option) => option !== '',
        );
        const ZERO = 0;
        if (specialPrograms.length > ZERO) {
            financialData.specialPrograms = specialPrograms;
        }

        if (
            financeType == financialTypes.LOAN ||
            financeType === financialTypes.BALLOON
        ) {
            let tradeInVehicle: any = {
                tradeInFinancing: {
                    grossTradeIn: netTradeInAmount,
                },
            };

            if (Number(netTradeInAmount) > ZERO) {
                tradeInVehicle = {
                    tradeInModel: tradeInModel.length ? tradeInModel : ' ',
                    tradeInMake: tradeInMake.length ? tradeInModel : ' ',
                    tradeInBody: tradeInBody.length ? tradeInBody : ' ',
                    tradeInYear: tradeInYear || ZERO,
                    ...tradeInVehicle,
                };
            }

            financialData = {
                ...financialData,
                cashPrice: purchasePrice,
                downPayment,
                monthlyPayment: 0,
                purchasePrice: 0,
                ...(Number(netTradeInAmount) > ZERO ? { tradeInVehicle } : {}),
            };
        } else {
            let tradeInVehicle;
            if (tradeInModel && tradeInMake && tradeInBody && tradeInYear) {
                tradeInVehicle = {
                    tradeInModel,
                    tradeInMake,
                    tradeInBody,
                    tradeInYear,
                    tradeInFinancing: {
                        // still to be defined
                    },
                };
            }
            financialData = {
                ...financialData,
                rebateAmount,
                adjustedCapCost,
                monthlyPayment,
                purchasePrice: grossCapCost,
                tradeInVehicle,
            };
        }
        setFinancialData(financialData);
    }

    function evaluateValidated(value: boolean) {
        if (validated !== value) setValidated(value);
        if (value) {
            setOpen(false);
            setDisabledForm(true);
            buildAndSetFinancialData();
            setFinanceValid(true);
        }
    }

    function handleTwoDecimalInput(
        value: string,
        setValue: React.Dispatch<React.SetStateAction<number>>,
    ) {
        value = value.replace(/[^\d.]/g, '');
        const parts = value.split('.');

        const int = parts[0] || '';
        const dec = parts[1] || '';

        const formatted =
            // eslint-disable-next-line no-magic-numbers
            int + (value.includes('.') ? '.' + dec.slice(0, 2) : '');

        const num = Number(formatted);
        // eslint-disable-next-line no-magic-numbers
        if (!isNaN(num) && num > 999999.99) return;
        setValue(num);
    }

    const cashPriceComponent = (
        <LayoutInput
            label={`${financeSection.CASH_PRICE_LABEL}`}
            tooltip={financeSection.CASH_PRICE_TOOLTIP}
            isError={errorPurchasePrice}
            errorLabel={financeSection.CASH_PRICE_ERROR}
            onChangeHandler={(event: any) => {
                handleTwoDecimalInput(event.target.value, setPurchasePrice);
                markCreditFormTouched();
            }}
            value={purchasePrice.toString()}
            min={0}
            max={999999.99}
            step='any'
            hideSpinner
        />
    );

    const monthlyPaymentComponent = (
        <LayoutInput
            label={`${financeSection.MONTHLY_PAY_LABEL}*`}
            isError={errorMonthlyPayment}
            errorLabel='Error validation'
            onChangeHandler={(event: any) => {
                handleTwoDecimalInput(event.target.value, setMonthlyPayment);
            }}
            value={monthlyPayment.toString()}
            min={0}
            max={999999.99}
            step='any'
            hideSpinner
        />
    );

    useEffect(() => {
        if (isEditMode) {
            buildAndSetFinancialData();
        }
    }, [isEditMode]);

    return (
        <>
            <FormSectionPage
                title={financeSection.TITLE}
                handlerSubmit={handlerSubmit}
                showIcon={validated}
                isOpen={isOpen}
                setOpen={setOpen}
                isDisabled={disabledForm}
                onEdit={onEdit}
                isEditMode={isEditMode}
                isBlocked={isBlocked}
                disablePrintAndSubmitBtn={disablePrintAndSubmitBtn}
            >
                {
                    <fieldset disabled={disabledForm}>
                        <div className={'financial-info'}>
                            {loanAmountError || rebateAmountError ? (
                                <div className='o-fieldset__row'>
                                    <div className='o-layout'>
                                        <div className='o-layout__item  u-1/1  u-1/1@xs'>
                                            <div className='c-form-field  c-form-field--no-floating  '>
                                                <section
                                                    id='js-notifications-group'
                                                    className='c-notifications-group  js-panelgroup js-panelgroup js-is-fx is-stacked js-is-ready u-mb-small u-mt-xsmall'
                                                >
                                                    <div
                                                        className={
                                                            'c-notification__container  js-notification-container'
                                                        }
                                                    >
                                                        <div className='c-notification  c-notification--error'>
                                                            <div className='c-notification__outer-wrap'>
                                                                <div className='c-notification__inner-wrap'>
                                                                    <div className='c-notification__content'>
                                                                        <p className='c-notification__text'>
                                                                            {
                                                                                financeSection.AMOUNT_MINIMUN_ERROR
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            <div className='o-fieldset__row'>
                                <div className='o-layout'>
                                    <LayoutCombo
                                        label={`${financeSection.TYPE_LABEL}*`}
                                        tooltip={financeSection.TYPE_TOOLTIP}
                                        defaultValue={financeType}
                                        options={financeOptions}
                                        name={'type'}
                                        selectedOption={financeType}
                                        onSelectHandler={(val: any) => {
                                            setFinanceType(val);
                                            markCreditFormTouched();
                                        }}
                                        isDisabled={disabledForm}
                                        placeHolder={''}
                                        isError={false}
                                        errorLabel=''
                                    />
                                    <LayoutInput
                                        label={`${financeSection.TERM_LABEL}`}
                                        type='text'
                                        tooltip={financeSection.TERM_TOOLTIP}
                                        isError={errorTerm}
                                        errorLabel={financeSection.TERM_ERROR}
                                        onChangeHandler={(event: any) => {
                                            const value = event.target.value;
                                            if (/^\d*$/.test(value)) {
                                                setTerm(
                                                    value === '' ? '' : +value,
                                                );
                                                markCreditFormTouched();
                                            }
                                        }}
                                        value={term.toString()}
                                        min={4}
                                        max={99}
                                        maxLength={2}
                                    />
                                </div>
                            </div>

                            <div className='o-fieldset__row'>
                                <div className='o-layout'>
                                    {vehicleType.toUpperCase() == 'NEW' ||
                                    vehicleType.toUpperCase() == 'DEMO' ? (
                                        <LayoutInput
                                            label={`${financeSection.MSRP_LABEL}*`}
                                            isError={errorMSRP}
                                            errorLabel={
                                                financeSection.MSRP_ERROR
                                            }
                                            onChangeHandler={(event: any) => {
                                                handleTwoDecimalInput(
                                                    event.target.value,
                                                    setMSRP,
                                                );
                                                markCreditFormTouched();
                                            }}
                                            value={msrp.toString()}
                                            min={0}
                                            max={999999.99}
                                            step='any'
                                            hideSpinner
                                        />
                                    ) : null}
                                    {financeType === financialTypes.LOAN ||
                                    financeType === financialTypes.BALLOON
                                        ? cashPriceComponent
                                        : monthlyPaymentComponent}
                                </div>
                            </div>

                            {financeType === financialTypes.LOAN ||
                            financeType === financialTypes.BALLOON ? (
                                <div className='o-fieldset__row'>
                                    <div className='o-layout'>
                                        <LayoutInput
                                            label={`${financeSection.TRADE_IN_AMOUNT_PAYMENT_LABEL}`}
                                            type='text'
                                            tooltip={
                                                financeSection.TRADE_IN_AMOUNT_PAYMENT_TOOLTIP
                                            }
                                            isError={errorTradeIn}
                                            errorLabel={`${financeSection.TRADE_IN_AMOUNT_PAYMENT_ERROR}`}
                                            onChangeHandler={(event: any) => {
                                                markCreditFormTouched();
                                                const value =
                                                    event.target.value;

                                                if (value === '') {
                                                    setNetTradeInAmount('');
                                                    return;
                                                }

                                                if (
                                                    /^-?\d*\.?\d{0,2}$/.test(
                                                        value,
                                                    )
                                                ) {
                                                    setNetTradeInAmount(
                                                        value.replace(
                                                            /^(-?)0+(?=\d)/,
                                                            '$1',
                                                        ),
                                                    );
                                                }
                                            }}
                                            onBlurHandler={() => {
                                                setNetTradeInAmount(
                                                    (prev: string) => {
                                                        const parsed =
                                                            parseFloat(prev);
                                                        return isNaN(parsed)
                                                            ? ZERO
                                                            : parsed;
                                                    },
                                                );
                                            }}
                                            value={netTradeInAmount.toString()}
                                            min={-99999.99}
                                            max={99999.99}
                                            step='any'
                                            hideSpinner
                                        />
                                        <LayoutInput
                                            label={`${financeSection.DOWN_PAYMENT_LABEL}`}
                                            tooltip={
                                                financeSection.DOWN_PAYMENT_TOOLTIP
                                            }
                                            isError={errorDownPayment}
                                            errorLabel={
                                                financeSection.DOWN_PAYMENT_ERROR
                                            }
                                            onChangeHandler={(event: any) => {
                                                markCreditFormTouched();
                                                handleTwoDecimalInput(
                                                    event.target.value,
                                                    setDownPayment,
                                                );
                                            }}
                                            value={downPayment.toString()}
                                            min={0}
                                            max={999999.99}
                                            step='any'
                                            hideSpinner
                                        />
                                    </div>
                                </div>
                            ) : null}

                            {financeType === financialTypes.LEASE ? (
                                <div className='o-fieldset__row'>
                                    <div className='o-layout'>
                                        <LayoutInput
                                            label={`${financeSection.GROSS_CAP_LABEL}*`}
                                            isError={errorGrossCap}
                                            errorLabel={
                                                financeSection.GROSS_CAP_ERROR
                                            }
                                            onChangeHandler={(event: any) => {
                                                markCreditFormTouched();
                                                handleTwoDecimalInput(
                                                    event.target.value,
                                                    setGrossCapCost,
                                                );
                                            }}
                                            value={grossCapCost.toString()}
                                            min={0}
                                            max={999999.99}
                                            step='any'
                                            hideSpinner
                                        />
                                        <LayoutInput
                                            label={`${financeSection.NET_CAP_PRICE_LABEL}*`}
                                            tooltip={
                                                financeSection.NET_CAP_TOOLTIP
                                            }
                                            isError={errorAdjustCap}
                                            errorLabel={
                                                financeSection.NET_CAP_ERROR
                                            }
                                            onChangeHandler={(event: any) => {
                                                markCreditFormTouched();
                                                handleTwoDecimalInput(
                                                    event.target.value,
                                                    setAdjustedCapCost,
                                                );
                                            }}
                                            value={adjustedCapCost.toString()}
                                            min={0}
                                            max={999999.99}
                                            step='any'
                                            hideSpinner
                                        />
                                    </div>
                                </div>
                            ) : null}
                            {financeType === financialTypes.LEASE ||
                            Number(netTradeInAmount) > ZERO ? (
                                <>
                                    <div className='o-fieldset__row'>
                                        <div className='o-layout'>
                                            <LayoutInput
                                                label={`${
                                                    financeSection.TRADE_IN_YEAR_LABEL
                                                }
                                                ${
                                                    (financeType ===
                                                        financialTypes.LOAN ||
                                                        financeType ===
                                                            financialTypes.BALLOON) &&
                                                    netTradeInAmount > ZERO
                                                        ? '*'
                                                        : ''
                                                }`}
                                                normalFit='1/4'
                                                isError={
                                                    errorTradeInYear ||
                                                    tradeInYearRequired
                                                }
                                                errorLabel={
                                                    errorTradeInYear
                                                        ? financeSection.TRADE_IN_YEAR_ERROR
                                                        : financeSection.ERROR_REQUIRED
                                                }
                                                onChangeHandler={(
                                                    event: any,
                                                ) => {
                                                    markCreditFormTouched();
                                                    setTradeInYear(
                                                        event.target.value ===
                                                            ''
                                                            ? ''
                                                            : Number(
                                                                  event.target
                                                                      .value,
                                                              ),
                                                    );
                                                }}
                                                value={tradeInYear.toString()}
                                                maxLength={
                                                    new Date().getFullYear() +
                                                    Number('1')
                                                }
                                                min={
                                                    new Date().getFullYear() -
                                                    Number('50')
                                                }
                                                step={'1'}
                                                hideSpinner
                                            />
                                            <LayoutInput
                                                label={`${
                                                    financeSection.TRADE_IN_MAKE_LABEL
                                                }
                                                ${
                                                    (financeType ===
                                                        financialTypes.LOAN ||
                                                        financeType ===
                                                            financialTypes.BALLOON) &&
                                                    netTradeInAmount > ZERO
                                                        ? '*'
                                                        : ''
                                                }`}
                                                normalFit='1/4'
                                                isError={
                                                    errorTradeInMake ||
                                                    tradeInMakeRequired
                                                }
                                                errorLabel={
                                                    errorTradeInMake
                                                        ? 'Error validation'
                                                        : financeSection.ERROR_REQUIRED
                                                }
                                                onChangeHandler={(
                                                    event: any,
                                                ) => {
                                                    setTradeInMake(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                value={tradeInMake}
                                                maxLength={30}
                                                type='text'
                                            />
                                            <LayoutInput
                                                label={`${
                                                    financeSection.TRADE_IN_MODEL_LABEL
                                                }
                                                ${
                                                    (financeType ===
                                                        financialTypes.LOAN ||
                                                        financeType ===
                                                            financialTypes.BALLOON) &&
                                                    netTradeInAmount > ZERO
                                                        ? '*'
                                                        : ''
                                                }`}
                                                normalFit='1/4'
                                                isError={
                                                    errorTradeInModel ||
                                                    tradeInModelRequired
                                                }
                                                errorLabel={
                                                    errorTradeInModel
                                                        ? 'Error validation'
                                                        : financeSection.ERROR_REQUIRED
                                                }
                                                onChangeHandler={(
                                                    event: any,
                                                ) => {
                                                    setTradeInModel(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                value={tradeInModel}
                                                maxLength={30}
                                                type='text'
                                            />
                                            <LayoutInput
                                                label={`${
                                                    financeSection.TRADE_IN_BODY_LABEL
                                                }
                                                ${
                                                    (financeType ===
                                                        financialTypes.LOAN ||
                                                        financeType ===
                                                            financialTypes.BALLOON) &&
                                                    netTradeInAmount > ZERO
                                                        ? '*'
                                                        : ''
                                                }`}
                                                normalFit='1/4'
                                                isError={
                                                    errorTradeInBody ||
                                                    tradeInBodyRequired
                                                }
                                                errorLabel={
                                                    errorTradeInBody
                                                        ? 'Error validation'
                                                        : financeSection.ERROR_REQUIRED
                                                }
                                                onChangeHandler={(
                                                    event: any,
                                                ) => {
                                                    setTradeInBody(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                value={tradeInBody}
                                                maxLength={30}
                                                type='text'
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : null}

                            <div className='o-fieldset__row'>
                                <div className='c-form-field  c-form-field--no-floating  '>
                                    <ItemLabel
                                        label={`${financeSection.SPECIAL_PROGRAM_LABEL}`}
                                        tooltip={
                                            financeSection.SPECIAL_PROGRAM_TOOLTIP
                                        }
                                        style={{
                                            fontSize: 16,
                                            color: '#1b1b1b',
                                        }}
                                    />
                                </div>
                            </div>

                            <div className='o-fieldset__row'>
                                <div className='o-layout'>
                                    <LayoutCombo
                                        label={`${financeSection.SPECIAL_PROGRAM_LABEL_2} Option 1`}
                                        defaultValue={''}
                                        options={optionsOneSP}
                                        name={'optionOne'}
                                        selectedOption={optionOne}
                                        onSelectHandler={(val: any) => {
                                            setOptionOne(val);
                                            markCreditFormTouched();
                                        }}
                                        isDisabled={disabledForm}
                                        placeHolder={creditApp.SELECT_OPTION}
                                    />
                                    <LayoutCombo
                                        label={`${financeSection.SPECIAL_PROGRAM_LABEL_2} Option 2`}
                                        defaultValue={''}
                                        options={optionsTwoSP}
                                        name={'optionTwo'}
                                        selectedOption={optionTwo}
                                        onSelectHandler={(val: any) => {
                                            setOptionTwo(val);
                                            markCreditFormTouched();
                                        }}
                                        isDisabled={disabledForm}
                                        placeHolder={creditApp.SELECT_OPTION}
                                    />
                                </div>
                            </div>
                        </div>
                    </fieldset>
                }
            </FormSectionPage>
        </>
    );
};

export default FinancialInfo;
